"use client"

import * as React from "react"
import { Avatar as AvatarPrimitive } from "radix-ui"

import { cn } from "@/custolink-newdesign/lib/utils"

function Avatar({
  className,
  size = "default",
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & {
  size?: "default" | "sm" | "lg"
}) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      className={cn(
        "cl:group/avatar cl:relative cl:flex cl:size-8 cl:shrink-0 cl:overflow-hidden cl:rounded-full cl:select-none cl:data-[size=lg]:size-10 cl:data-[size=sm]:size-6",
        className
      )}
      {...props}
    />
  )
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("cl:aspect-square cl:size-full", className)}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "cl:flex cl:size-full cl:items-center cl:justify-center cl:rounded-full cl:bg-muted cl:text-sm cl:text-muted-foreground cl:group-data-[size=sm]/avatar:text-xs",
        className
      )}
      {...props}
    />
  )
}

function AvatarBadge({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        "cl:absolute cl:right-0 cl:bottom-0 cl:z-10 cl:inline-flex cl:items-center cl:justify-center cl:rounded-full cl:bg-primary cl:text-primary-foreground cl:ring-2 cl:ring-background cl:select-none",
        "cl:group-data-[size=sm]/avatar:size-2 cl:group-data-[size=sm]/avatar:[&>svg]:hidden",
        "cl:group-data-[size=default]/avatar:size-2.5 cl:group-data-[size=default]/avatar:[&>svg]:size-2",
        "cl:group-data-[size=lg]/avatar:size-3 cl:group-data-[size=lg]/avatar:[&>svg]:size-2",
        className
      )}
      {...props}
    />
  )
}

function AvatarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        "cl:group/avatar-group cl:flex cl:-space-x-2 cl:*:data-[slot=avatar]:ring-2 cl:*:data-[slot=avatar]:ring-background",
        className
      )}
      {...props}
    />
  )
}

function AvatarGroupCount({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        "cl:relative cl:flex cl:size-8 cl:shrink-0 cl:items-center cl:justify-center cl:rounded-full cl:bg-muted cl:text-sm cl:text-muted-foreground cl:ring-2 cl:ring-background cl:group-has-data-[size=lg]/avatar-group:size-10 cl:group-has-data-[size=sm]/avatar-group:size-6 cl:[&>svg]:size-4 cl:group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 cl:group-has-data-[size=sm]/avatar-group:[&>svg]:size-3",
        className
      )}
      {...props}
    />
  )
}

export {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarBadge,
  AvatarGroup,
  AvatarGroupCount,
}
